// #32 — a cast of a post (the dock's own or watched cast whose queue carries a postId) counts once per cast in
// Recent plays. The post is fetched (cached for a minute) for its title and thumbnail; a failed fetch records nothing.
import { useEffect, useRef } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { getPost } from "../api/content";
import { gcNameFromCache } from "../components/wall/wallName";
import { recentOf } from "../lib/recentPlays";
import { useCastQueueStore } from "../store/castQueueStore";
import { useRecentPlaysStore } from "../store/recentPlaysStore";
import { useAuthStore } from "../store/authStore";
import { useDockCasts } from "./useDockCasts";
import { wallPostKey } from "./useWallPost";
import logger from "../lib/logger";

export function useCastRecents(): void {
  const qc = useQueryClient();
  const casts = useDockCasts();
  const queues = useCastQueueStore((s) => s.bySlot);
  const userId = useAuthStore((s) => s.userId);
  const seen = useRef(new Set<string>());
  // Another account starts its own once-per-cast record (same tab, same cast still playing).
  const seenFor = useRef(userId);
  if (seenFor.current !== userId) {
    seenFor.current = userId;
    seen.current = new Set();
  }

  useEffect(() => {
    if (!userId) return;
    for (const cast of casts) {
      const postId = queues[cast.slotId]?.queue.postId;
      if (!postId) continue;
      const pair = `${cast.key}|${postId}`;
      if (seen.current.has(pair)) continue;
      seen.current.add(pair);
      // Recorded for the account that saw the cast: a fetch that outlives a logout / account switch records nothing.
      const forUserId = userId;
      qc.fetchQuery({ queryKey: wallPostKey(postId), queryFn: () => getPost(postId), staleTime: 60_000 }).then(
        (post) => {
          if (useAuthStore.getState().userId !== forUserId) return;
          useRecentPlaysStore.getState().record(recentOf(post, gcNameFromCache(qc, cast.gcId), Date.now()));
        },
        (e: unknown) => logger.warn("[Recent] cast post fetch failed", postId, e),
      );
    }
  }, [casts, queues, qc, userId]);
}
